import React from 'react';
import { Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { CartState } from '../types';

interface PriceCalculation {
  subtotalExclVat: number;
  shippingCost: number;
  originalShippingCost: number;
  discount: {
    appliedTier: any;
    isFixedDiscount: boolean;
    userDiscountPercentage: number;
    shippingDiscountPercentage: number;
    tierDiscountPercentage: number;
    shippingDiscountAmount: number;
    tierDiscountAmount: number;
  };
  vat: {
    rate: number;
    onProducts: number;
    onShipping: number;
    total: number;
  };
  totals: {
    exclVat: number;
    inclVat: number;
  };
  shipping: {
    totalQuantity: number;
    colloCount: number;
    costPerCollo: number;
  };
  credit_limit: {
    credit_limit: number | null;
    credit_limit_source: string;
    outstanding_amount: number;
    available_credit: number | null;
    credit_status: string;
  };
}

interface CartSummaryProps {
  cartState: CartState;
  priceCalculation: PriceCalculation | null;
  isCalculatingPrice?: boolean;
  showArticleCount?: boolean;
  maxArticlesPerOrder?: number | null;
  articleCountColor?: string;
  compact?: boolean;
}

const CartSummary: React.FC<CartSummaryProps> = ({
  cartState,
  priceCalculation,
  isCalculatingPrice = false,
  showArticleCount = false,
  maxArticlesPerOrder = null,
  articleCountColor = 'dark',
  compact = false
}) => {
  const { t } = useTranslation();

  const totalArticles = cartState.items.reduce((total, item) => total + item.quantity, 0);
  const creditInfo = priceCalculation?.credit_limit;
  const currentTotal = priceCalculation?.totals.inclVat || cartState.total;

  const creditAfterOrder = creditInfo?.available_credit !== null && creditInfo?.available_credit !== undefined
    ? creditInfo.available_credit - currentTotal
    : null;

  const creditLimitExceeded = creditInfo &&
    creditInfo.available_credit !== null &&
    (creditAfterOrder !== null && creditAfterOrder < 0);

  return (
    <>
      {/* Article count */}
      {showArticleCount && (
        <div className="d-flex justify-content-between mb-1">
          <span>{t('cart.numberOfArticles')}</span>
          <span className={`fw-bold text-${articleCountColor}`}>
            {maxArticlesPerOrder
              ? `${totalArticles}/${maxArticlesPerOrder}`
              : totalArticles
            }
          </span>
        </div>
      )}

      {/* Price calculation */}
      {isCalculatingPrice ? (
        <div className="text-center mb-2">
          <small className="text-muted">{t('cart.calculatingPrice', 'Calculating prices...')}</small>
        </div>
      ) : priceCalculation ? (
        <>
          <div className="d-flex justify-content-between mb-1">
            <span>
              {t('cart.subtotal')}
              {priceCalculation.discount.userDiscountPercentage > 0 &&
                ` (${t('cart.inclDiscount', { percentage: priceCalculation.discount.userDiscountPercentage })})`
              }
            </span>
            <span>€{priceCalculation.subtotalExclVat.toFixed(2)}</span>
          </div>
          {priceCalculation.discount.tierDiscountAmount > 0 && (
            <div className="d-flex justify-content-between mb-1 text-success">
              <span>{t('cart.volumeDiscount')} ({priceCalculation.discount.tierDiscountPercentage}%)</span>
              <span>-€{priceCalculation.discount.tierDiscountAmount.toFixed(2)}</span>
            </div>
          )}
          <div className="d-flex justify-content-between mb-1">
            <span>{t('cart.shipping')} ({priceCalculation.shipping.colloCount} collo)</span>
            <span>€{priceCalculation.shippingCost.toFixed(2)}</span>
          </div>
          <div className="d-flex justify-content-between mb-1">
            <span>{t('cart.vatLabel', { rate: Math.round(priceCalculation.vat.rate * 100) })}</span>
            <span>€{priceCalculation.vat.total.toFixed(2)}</span>
          </div>
          <div className="d-flex justify-content-between fw-bold mb-2">
            <span>{t('cart.total')}</span>
            <span>€{priceCalculation.totals.inclVat.toFixed(2)}</span>
          </div>
        </>
      ) : (
        <>
          <div className="d-flex justify-content-between mb-1">
            <span>{t('cart.subtotal')}</span>
            <span>€{cartState.subtotal.toFixed(2)}</span>
          </div>
          <div className="d-flex justify-content-between mb-1">
            <span>{t('cart.vat')}</span>
            <span>€{cartState.tax.toFixed(2)}</span>
          </div>
          <div className="d-flex justify-content-between fw-bold mb-2">
            <span>{t('cart.total')}</span>
            <span>€{cartState.total.toFixed(2)}</span>
          </div>
        </>
      )}

      {/* Credit Limit Info */}
      {creditInfo && creditInfo.available_credit !== null && cartState.items.length > 0 && (
        <Alert
          variant={creditAfterOrder !== null && creditAfterOrder < 0 ? "danger" : "success"}
          className={`mb-2 py-2 px-3 ${compact ? '' : 'cart-alert'}`}
        >
          <small>
            {creditAfterOrder !== null && creditAfterOrder < 0 ? (
              <>
                <i className="fas fa-exclamation-triangle me-1"></i>
                {t('cart.creditLimit.exceeded')}<br />
                {t('cart.creditLimit.available')}: €{creditInfo.available_credit.toFixed(2)}<br />
                {t('cart.creditLimit.orderTotal')}: €{currentTotal.toFixed(2)}
              </>
            ) : (
              <>
                <i className="fas fa-check-circle me-1"></i>
                {t('cart.creditLimit.availableAfterOrder')}: €{creditAfterOrder?.toFixed(2)}
              </>
            )}
          </small>
        </Alert>
      )}
    </>
  );
};

export default CartSummary;
